import { Check, ChevronDown } from 'lucide-react';
import type { Investment, InvestmentStatus } from '@/api/types';
import { useUpdateInvestment } from '@/api/hooks';
import { useCanEdit } from '@/auth/account';
import { cn } from '@/lib/cn';
import { STATUS_META, STATUS_ORDER } from '@/lib/status';
import { StatusBadge } from './ui/Badge';

/**
 * Status of an investment that editors can change right in the list: a badge-styled select plus a
 * one-click „erledigt“ button. Viewers just see the badge.
 */
export function StatusControl({ investment }: { investment: Pick<Investment, 'id' | 'title' | 'status'> }) {
  const canEdit = useCanEdit();
  const update = useUpdateInvestment();
  if (!canEdit) return <StatusBadge status={investment.status} />;

  const meta = STATUS_META[investment.status];
  const change = (status: InvestmentStatus) => {
    if (status !== investment.status) update.mutate({ id: investment.id, status });
  };
  const closed = investment.status === 'done' || investment.status === 'cancelled';

  return (
    <div className="flex shrink-0 items-center gap-1">
      <label className="relative inline-flex items-center">
        <span className="sr-only">Status von {investment.title}</span>
        <span aria-hidden="true" className="pointer-events-none absolute left-2 h-1.5 w-1.5 rounded-full" style={{ background: meta.dot }} />
        <select
          value={investment.status}
          disabled={update.isPending}
          onChange={(event) => change(event.target.value as InvestmentStatus)}
          className={cn(
            'cursor-pointer appearance-none rounded-full border-0 py-0.5 pr-5 pl-4.5 text-[11px] font-medium whitespace-nowrap outline-offset-2 disabled:opacity-60',
            meta.className,
          )}
        >
          {STATUS_ORDER.map((status) => (
            <option key={status} value={status}>
              {STATUS_META[status].label}
            </option>
          ))}
        </select>
        <ChevronDown aria-hidden="true" size={11} className="pointer-events-none absolute right-1.5 opacity-60" />
      </label>
      {closed ? (
        <span className="w-7" aria-hidden="true" />
      ) : (
        <button
          type="button"
          aria-label={`${investment.title} als erledigt markieren`}
          title="Als erledigt markieren"
          disabled={update.isPending}
          onClick={() => change('done')}
          className="grid h-7 w-7 place-items-center rounded-full text-[var(--ink-3)] transition-colors hover:bg-emerald-500/14 hover:text-emerald-700 disabled:opacity-50 dark:hover:text-emerald-300"
        >
          <Check size={15} />
        </button>
      )}
    </div>
  );
}
